import React from 'react';
import { format } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import OrderCustomizationSummary from './OrderCustomizationSummary';
import * as FiIcons from 'react-icons/fi';

const { FiPrinter, FiShoppingBag, FiTruck, FiMapPin, FiInfo, FiShield, FiCreditCard, FiDollarSign } = FiIcons;

const formatPrice = (price) => {
  if (price === undefined || price === null) return '$0.00';
  const val = typeof price === 'string' ? price.replace('$', '') : price;
  return `$${parseFloat(val).toFixed(2)}`;
};

const numericPrice=(price)=>{
  if (typeof price==='number') return price;

  return Number.parseFloat(
    String(price ?? '').replace(/[^0-9.-]/g,'')
  ) || 0;
};

const InvoiceDocument = ({ order, onClose }) => {
  const handlePrint = () => {
    window.print();
  };

  if (!order) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-[100] p-4 print:p-0 print:bg-white print:static print:inset-auto">
      <div className="bg-white rounded-[3rem] max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl print:shadow-none print:rounded-none print:max-h-none print:w-full">
        {/* Header - Hidden on Print */}
        <div className="flex items-center justify-between p-8 border-b border-slate-50 print:hidden">
          <div>
            <h3 className="text-xl font-black text-slate-900 tracking-tight">Invoice Preview</h3>
            <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mt-1">Ready for Print or PDF</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handlePrint}
              className="flex items-center px-6 py-3 bg-indigo-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
            >
              <SafeIcon icon={FiPrinter} className="mr-2" />
              Print / Save PDF
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-slate-100 text-slate-600 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-all"
            >
              Close
            </button>
          </div>
        </div>

        {/* Invoice Content */}
        <div className="flex-1 overflow-y-auto p-12 print:p-0 no-scrollbar">
          <div id="printable-invoice" className="bg-white">
            {/* Branding & Order ID */}
            <div className="flex flex-col md:flex-row justify-between items-start mb-16 gap-8">
              <div>
                <img src="https://assets.sohlocal.com/Branding/logo.png" alt="SohLocal" className="h-12 w-auto mb-6" />
                <h1 className="text-4xl font-black text-slate-900 tracking-tighter">INVOICE</h1>
                <p className="text-slate-400 font-bold uppercase tracking-[0.2em] text-[10px] mt-2">Order ID: {order.id.slice(0, 8).toUpperCase()}</p>
              </div>
              <div className="text-left md:text-right">
                <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-1">Fulfillment Date</p>
                <p className="text-xl font-black text-slate-900">{order.requested_date ? format(new Date(order.requested_date), 'MMMM do, yyyy') : 'TBD'}</p>
                <p className="text-sm font-bold text-slate-400 mt-1 uppercase tracking-wider">{order.delivery_method || 'Pickup'}</p>
              </div>
            </div>

            {/* Parties */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
              <div>
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-4 border-b border-slate-100 pb-2">From (Seller)</h4>
                <p className="text-lg font-black text-slate-900">{order.seller?.business_name}</p>
                <p className="text-slate-500 font-medium mt-1">{order.seller?.location}</p>
                <p className="text-slate-500 font-medium">{order.seller?.email}</p>
              </div>
              <div>
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-4 border-b border-slate-100 pb-2">Bill To (Buyer)</h4>
                <p className="text-lg font-black text-slate-900">{order.buyer?.name || 'Valued Neighbor'}</p>
                <p className="text-slate-500 font-medium mt-1">{order.buyer?.email}</p>
                {order.buyer?.phone && <p className="text-slate-500 font-medium">{order.buyer.phone}</p>}
              </div>
            </div>

            {/* Line Items */}
            <table className="w-full mb-12">
              <thead>
                <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b-2 border-slate-900">
                  <th className="py-4 text-left">Description</th>
                  <th className="py-4 text-center">Qty</th>
                  <th className="py-4 text-right">Unit Price</th>
                  <th className="py-4 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(order.items || []).map((item, idx) => (
                  <tr key={idx}>
                    <td className="py-6">
                      <p className="font-black text-slate-900">{item.title}</p>
                      <OrderCustomizationSummary item={item} />
                    </td>
                    <td className="py-6 text-center font-bold text-slate-600">{item.quantity}</td>
                    <td className="py-6 text-right font-bold text-slate-600">{formatPrice(item.price)}</td>
                    <td className="py-6 text-right font-black text-slate-900">
                      {formatPrice(numericPrice(item.price) * Number(item.quantity || 0))}
                    </td>
                  </tr>
                ))}
                {order.delivery_fee > 0 && (
                  <tr className="bg-indigo-50/30 print:bg-slate-50">
                    <td className="py-6 px-4">
                      <p className="font-black text-indigo-900">Hand Delivery Service</p>
                    </td>
                    <td className="py-6 text-center font-bold text-indigo-600">1</td>
                    <td className="py-6 text-right font-bold text-indigo-600">{formatPrice(order.delivery_fee)}</td>
                    <td className="py-6 text-right font-black text-indigo-900 pr-4">{formatPrice(order.delivery_fee)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Totals & Notes */}
            <div className="flex flex-col md:flex-row justify-between gap-12">
              <div className="flex-1">
                {/* Payment Indication on Invoice */}
                <div className={`p-6 rounded-2xl border mb-6 ${order.payment_mode === 'platform' ? 'bg-indigo-50 border-indigo-100' : 'bg-emerald-50 border-emerald-100'}`}>
                  <div className="flex items-center mb-2">
                    <SafeIcon icon={order.payment_mode === 'platform' ? FiCreditCard : FiDollarSign} className={`mr-2 text-sm ${order.payment_mode === 'platform' ? 'text-indigo-600' : 'text-emerald-600'}`} />
                    <span className={`text-[10px] font-black uppercase tracking-widest ${order.payment_mode === 'platform' ? 'text-indigo-900' : 'text-emerald-900'}`}>Payment Status</span>
                  </div>
                  <p className={`text-sm font-bold ${order.payment_mode === 'platform' ? 'text-indigo-700' : 'text-emerald-700'}`}>
                    {order.payment_mode === 'platform' ? "Paid via SohLocal Secure Checkout" : "Pay Seller Directly (Cash/E-Transfer/PayPal)"}
                  </p>
                </div>

                {order.order_notes && (
                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 mb-6">
                    <div className="flex items-center mb-2 text-slate-400">
                      <SafeIcon icon={FiInfo} className="mr-2 text-xs" />
                      <span className="text-[9px] font-black uppercase tracking-widest">Order Notes</span>
                    </div>
                    <p className="text-sm font-medium text-slate-600 leading-relaxed italic">"{order.order_notes}"</p>
                  </div>
                )}
                
                <div className="space-y-4">
                  <div className="flex items-center text-slate-400">
                    <SafeIcon icon={FiShield} className="mr-2 text-xs" />
                    <span className="text-[8px] font-black uppercase tracking-widest">Official SOHLocal Transaction Record</span>
                  </div>
                  <p className="text-[8px] text-slate-400 leading-relaxed uppercase font-bold max-w-sm">
                    {order.payment_mode === 'platform' ? "This transaction is protected by SohLocal buyer protection. Payment was processed via our secure gateway." : "This is an informal community invoice. SohLocal did not process this payment. Please coordinate directly with the seller."}
                  </p>
                </div>
              </div>
              <div className="w-full md:w-64 space-y-3">
                <div className="flex justify-between text-sm font-bold text-slate-400 uppercase tracking-widest">
                  <span>Subtotal</span>
                  <span>{formatPrice(order.total_amount - (order.delivery_fee || 0))}</span>
                </div>
                {order.delivery_fee > 0 && (
                  <div className="flex justify-between text-sm font-bold text-indigo-600 uppercase tracking-widest">
                    <span>Delivery</span>
                    <span>{formatPrice(order.delivery_fee)}</span>
                  </div>
                )}
                <div className="flex justify-between items-end pt-4 border-t border-slate-100">
                  <span className="text-xs font-black text-slate-900 uppercase tracking-widest">Amount Due</span>
                  <span className="text-3xl font-black text-indigo-600 leading-none">{formatPrice(order.total_amount)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDocument;